/**
 * How much of a campaign goes out on its first day, and how long the rest
 * takes.
 *
 * Each inbox has an effective daily limit: its configured cap, or the warmup
 * ramp when that is lower. A campaign whose audience is larger than the
 * inboxes' combined daily limit does not fail and does not stop; the contacts
 * that do not fit are held and sent on later days (see rotation_email.md and
 * daily_capacity_spill.md). That is worth saying before launch rather than
 * leaving the user to infer it from a send count that stalls.
 *
 * Deliberately a forecast and nothing more. Sending is gated by an atomic
 * per-inbox claim at send time, never by a number computed here.
 */
import type { SenderEmail } from '@/types/senderEmail'

export interface Pacing {
  /** Combined effective daily limit of the chosen inboxes. */
  perDay: number
  /** Contacts selected, or null when the audience size is not known yet. */
  audience: number | null
  /** How many go out on the first day. */
  firstDay: number
  /** How many wait for later days. 0 when everything fits. */
  deferred: number
  /** Whole days the campaign needs, including the first. */
  days: number
}

/**
 * The inboxes' combined daily send capacity.
 *
 * Falls back to the configured limit for an inbox the server did not report an
 * effective limit for, so an older backend under-reports rather than showing 0.
 */
export function dailyCapacity(selectedIds: number[], all: SenderEmail[]): number {
  return selectedIds.reduce((sum, id) => {
    const sender = all.find(s => s.id === id)
    if (!sender) return sum
    const limit = sender.effective_daily_limit ?? sender.daily_send_limit ?? 0
    return sum + (Number.isFinite(limit) && limit > 0 ? limit : 0)
  }, 0)
}

/** Contacts in the chosen lists. Null for "all contacts", whose size the
 *  wizard does not fetch. An upper bound: the same contact can sit in two
 *  lists and the backend counts them once. */
export function audienceSize(
  audienceType: 'list' | 'all',
  selectedListIds: number[],
  lists: { id: number; total_contacts: number }[],
): number | null {
  if (audienceType !== 'list') return null
  if (selectedListIds.length === 0) return null
  return selectedListIds.reduce((sum, id) => {
    const list = lists.find(l => l.id === id)
    return sum + (list?.total_contacts ?? 0)
  }, 0)
}

export function pacing(perDay: number, audience: number | null): Pacing | null {
  if (perDay <= 0) return null
  if (audience === null) {
    return { perDay, audience: null, firstDay: perDay, deferred: 0, days: 1 }
  }
  const firstDay = Math.min(audience, perDay)
  return {
    perDay,
    audience,
    firstDay,
    deferred: Math.max(0, audience - perDay),
    days: Math.max(1, Math.ceil(audience / perDay)),
  }
}

const n = (value: number) => value.toLocaleString()

/**
 * The sentence shown once the audience is chosen, or null when there is
 * nothing useful to say yet (no inbox picked, or no capacity known).
 */
export function pacingMessage(p: Pacing | null): string | null {
  if (!p) return null

  if (p.audience === null) {
    return (
      `Your selected inboxes can send about ${n(p.perDay)} emails a day. ` +
      `Any contacts beyond that go out on later days.`
    )
  }

  if (p.deferred === 0) {
    return (
      `All ${n(p.audience)} contacts should go out on the first day. ` +
      `Your selected inboxes can send about ${n(p.perDay)} a day.`
    )
  }

  return (
    `About ${n(p.firstDay)} of ${n(p.audience)} contacts go out on the first day, ` +
    `because that is what your selected inboxes can send in a day. The remaining ` +
    `${n(p.deferred)} are held and sent on later days, roughly ${p.days} days in total. ` +
    `The campaign keeps running until every contact has been sent; it does not pause. ` +
    `Add another inbox to raise the daily volume.`
  )
}
